import FractalShader from "./FractalShader";

export default function Hero() {
  return (
    <section className="relative w-full h-screen min-h-[700px] overflow-hidden bg-black">
      {/* SVG filter for real refractive glass distortion */}
      <svg
        className="absolute w-0 h-0 pointer-events-none"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <filter id="glass-distortion" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.012 0.018"
              numOctaves="2"
              seed="7"
              result="noise"
            />
            <feGaussianBlur in="noise" stdDeviation="1.2" result="blurredNoise" />
            <feDisplacementMap
              in="SourceGraphic"
              in2="blurredNoise"
              scale="28"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      {/* Animated shader background */}
      <FractalShader />

      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60 pointer-events-none"></div>

      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)",
        }}
      ></div>

      {/* Content */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-4 md:px-8 pt-24 pb-16">
        <div className="w-full max-w-4xl mx-auto text-center flex flex-col items-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="text-xs text-white/80 tracking-wide whitespace-nowrap">
              Now in public beta · v1.0
            </span>
          </div>

          {/* Headline */}
          <h1
            className="text-white text-4xl sm:text-5xl md:text-7xl leading-[1.05] tracking-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}
          >
            Build worlds that
            <br />
            <span className="italic text-white">feel alive.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-white/70 text-base md:text-lg max-w-2xl mb-10 leading-relaxed">
            Aurora is a real-time creative engine for artists, designers and
            developers. Sculpt shaders, orchestrate motion, and ship immersive
            experiences — all from the browser.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center gap-3 mb-14">
            <a
              href="#get-started"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              Start building free
              <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
            </a>
            <a
              href="#demo"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm text-white hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <i className="ri-play-circle-line text-lg"></i>
              Watch demo
            </a>
          </div>

          {/* Trust row */}
          <div className="flex flex-col items-center gap-4">
            <p className="text-xs text-white/50 tracking-widest uppercase">
              Trusted by creative teams at
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/60">
              <span
                className="text-lg tracking-tight whitespace-nowrap"
                style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500 }}
              >
                Nebula
              </span>
              <span className="text-lg font-serif italic whitespace-nowrap">Vantage</span>
              <span
                className="text-lg tracking-widest whitespace-nowrap"
                style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}
              >
                NORTH*
              </span>
              <span className="text-lg tracking-tight whitespace-nowrap italic">
                lumen.
              </span>
              <span
                className="text-lg tracking-tight whitespace-nowrap"
                style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500 }}
              >
                Prism/AI
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}